// FlixCard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import FlixPreview from '../container/FlixPreview.js';
import WithMessageMetaData from '../container/WithMessageMetaData.js';
import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import HighlightSubstring from '../component/HighlightSubstring.jsx';
import Tooltip from '../component/Tooltip.jsx';
import TextEllipsis from '../style/TextEllipsis.js';
import formatNumber from '../resource/formatNumber.js';
import UnLockIconSrc from '../../img/ic_unlock_xs.svg';
import LikeIconSrc from '../../img/ic_like_xs.svg';
import ProIconSrc from '../../img/ic_pro_badge.svg';
import ViewersIconSrc from '../../img/ic_streaming_view_xs.svg';
import DiamondIconSource from '../../img/ic_diamond.svg';
import { getKeySystem } from '../resource/getDrmInfo.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import {
  LIVESTREAM_TYPE_LIVE_BADGE,
  ONLINE_TYPE_BIGGER_DOT,
} from '../resource/userStatusIndicatorConstants.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { textColor, color } from '../style/variables.js';

export class FlixCard extends React.PureComponent {
  state = {
    isHovered: false,
  };
  nextTicks = [];

  fetchDrmLicenseAvailability = () => {
    const {
      messageId,
      canViewMedia,
      isIntersecting,
      fetchDrmLicenseAvailability,
    } = this.props;

    if (!messageId || canViewMedia || !isIntersecting) return;

    this.nextTicks.push(
      setTimeout(() =>
        fetchDrmLicenseAvailability({
          messageId,
          keySystem: getKeySystem(),
        })
      )
    );
  };

  handleOnMouseEnter = () => {
    this.setState({ isHovered: true });
  };

  handleOnMouseLeave = () => {
    this.setState({ isHovered: false });
  };
  trackUserClick = buttonId => {
    if (this.props.shouldTrackEvent) {
      const { categoryIndex, categoryId, username, messageId, badges } =
        this.props;
      trackMixpanel({
        type: EventTypes.USER_CLICKED,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          buttonId,
          messageId,
          extraProps: {
            'user.username': username,
            'discover.index': categoryIndex,
            'discover.category': categoryId,
            badges: badges?.join(','),
          },
        },
      });
    }
  };
  renderUsernameTooltip = () => {
    const { username } = this.props;

    return <TooltipWrapper>{username}</TooltipWrapper>;
  };
  renderAvatar = () => {
    const { userId, enableTooltip, shouldShowOnline, shouldShowLiveStream } =
      this.props;

    return (
      <AvatarWrapper>
        <Tooltip
          position="bottom-center"
          renderHint={this.renderUsernameTooltip}
          trigger="hover"
          disabled={!enableTooltip}
        >
          <AvatarBlock>
            <UserAvatarWithStatus
              id={userId}
              size={36}
              circleOffset={2}
              liveStreamType={LIVESTREAM_TYPE_LIVE_BADGE}
              livestreamSize={14}
              onlineType={ONLINE_TYPE_BIGGER_DOT}
              shouldShowLiveStream={shouldShowLiveStream}
              shouldShowOnline={shouldShowOnline}
            />
          </AvatarBlock>
        </Tooltip>
      </AvatarWrapper>
    );
  };

  handleLinkClick = () => {
    this.trackUserClick(ButtonId.All.ButtonFlixCard);
  };

  getIsPlaceholder = () => {
    const { messageId } = this.props;
    return messageId == null;
  };

  renderInfoBlock() {
    const {
      messageId,
      title,
      caption,
      shouldShowUsername,
      shouldShowAvatar,
      isPro,
      hasFreePeek,
      isOnFreezone,
      hasAmateur,
      shouldShowUnlockCount,
      username,
      userId,
      matchedString,
      unlockPrice,
      canViewMedia,
    } = this.props;
    const isPlaceholder = this.getIsPlaceholder();
    const shouldShowViewCount =
      !shouldShowUnlockCount || (hasFreePeek && isOnFreezone) || hasAmateur;

    const LinkProps = {
      to:
        isOnFreezone && hasFreePeek
          ? `/freezone/${messageId}`
          : `/post/${messageId}`,

      onClick: this.handleLinkClick,
      as: Link,
    };

    return (
      <InfoWrapper>
        <Info>
          {shouldShowAvatar && this.renderAvatar()}
          <InfoRight {...(messageId ? LinkProps : {})}>
            <Caption isPlaceholder={isPlaceholder}>
              {isPro && (
                <ProIconWrapper>
                  <ProIcon
                    src={ProIconSrc}
                    width="12"
                    height="12"
                    alt="pro icon"
                  />
                </ProIconWrapper>
              )}
              <HighlightSubstring substring={matchedString}>
                {isPlaceholder ? '' : title || caption || '-'}
              </HighlightSubstring>
            </Caption>
            {shouldShowUsername && (
              <Username isPlaceholder={isPlaceholder}>
                <UsernameText>
                  {isPlaceholder ? '' : `@${username}`}
                </UsernameText>
                <UserStatusIndicatorWrapper>
                  <UserStatusIndicator
                    userId={userId}
                    shouldShowLiveStream
                    livestreamSize={12}
                    shouldShowOnline
                  />
                </UserStatusIndicatorWrapper>
              </Username>
            )}
            {isPlaceholder ? null : (
              <>
                <WithMessageMetaData messageId={messageId}>
                  {({ likePercentage, unlocks, viewCount }) => (
                    <MetaData>
                      {shouldShowViewCount ? (
                        <>
                          <ViewsIcon
                            src={ViewersIconSrc}
                            alt="Views"
                            width="16"
                            height="16"
                          />
                          <ViewsNumber>{viewCount}</ViewsNumber>
                        </>
                      ) : (
                        <>
                          <UnLockIcon
                            src={UnLockIconSrc}
                            alt="Unlock"
                            width="16"
                            height="16"
                          />
                          <UnLockNumber>
                            {unlocks ? formatNumber(unlocks) : '-'}
                          </UnLockNumber>
                        </>
                      )}
                      <LikeIcon
                        src={LikeIconSrc}
                        alt="Like"
                        width="16"
                        height="16"
                      />
                      <LikeRateNumber>
                        {likePercentage ? `${likePercentage}%` : '-'}
                      </LikeRateNumber>
                    </MetaData>
                  )}
                </WithMessageMetaData>
                {unlockPrice > 0 && !canViewMedia && (
                  <UnlockPriceWrapper>
                    <UnlockPriceImg src={DiamondIconSource} alt="diamond" />
                    <UnlockPriceText>{unlockPrice}</UnlockPriceText>
                  </UnlockPriceWrapper>
                )}
              </>
            )}
          </InfoRight>
        </Info>
      </InfoWrapper>
    );
  }

  fetchMessageDetail = () => {
    const { messageId, badges, fetchMessageDetail } = this.props;
    if (messageId && !badges) {
      this.nextTicks.push(setTimeout(() => fetchMessageDetail({ messageId })));
    }
  };

  componentDidMount() {
    this.fetchMessageDetail();
    this.fetchDrmLicenseAvailability();
  }

  componentDidUpdate(prevProps) {
    const { messageId, isAuthed, canViewMedia, isIntersecting } = this.props;
    if (isAuthed !== prevProps.isAuthed || messageId !== prevProps.messageId) {
      this.fetchMessageDetail();
    }

    if (
      messageId !== prevProps.messageId ||
      canViewMedia !== prevProps.canViewMedia ||
      isIntersecting !== prevProps.isIntersecting
    ) {
      this.fetchDrmLicenseAvailability();
    }
  }

  componentWillUnmount() {
    this.nextTicks.forEach(clearTimeout);
  }

  render() {
    const {
      style,
      messageId,
      categoryId,
      categoryIndex,
      shouldTrackEvent,
      shouldUseLazyImage,
      shouldAnimateLoader,
      shouldPlay,
    } = this.props;
    const { isHovered } = this.state;

    return (
      <StyledFlixCard
        onMouseEnter={this.handleOnMouseEnter}
        onMouseLeave={this.handleOnMouseLeave}
        style={style}
      >
        <FlixPreview
          messageId={messageId}
          shouldPlay={isHovered || shouldPlay}
          categoryId={categoryId}
          categoryIndex={categoryIndex}
          shouldTrackCardEvent={shouldTrackEvent}
          shouldUseLazyImage={shouldUseLazyImage}
          shouldAnimateLoader={shouldAnimateLoader}
          shouldUseLink
          shouldShowLoader
        />
        {this.renderInfoBlock()}
      </StyledFlixCard>
    );
  }
}

FlixCard.propTypes = {
  categoryIndex: PropTypes.number,
  categoryId: PropTypes.string,
  title: PropTypes.string,
  caption: PropTypes.string,
  messageId: PropTypes.string,
  shouldShowUsername: PropTypes.bool,
  shouldShowAvatar: PropTypes.bool,
  shouldAnimateLoader: PropTypes.bool,
  shouldTrackEvent: PropTypes.bool,
  shouldUseLazyImage: PropTypes.bool,
  shouldPlay: PropTypes.bool,
  enableTooltip: PropTypes.bool,
  isPro: PropTypes.bool,
  hasFreePeek: PropTypes.bool,
  isOnFreezone: PropTypes.bool,
  hasAmateur: PropTypes.bool,
  isAuthed: PropTypes.bool,
  shouldShowUnlockCount: PropTypes.bool,
  userId: PropTypes.string,
  username: PropTypes.string,
  badges: PropTypes.array,
  style: PropTypes.object,
  fetchMessageDetail: PropTypes.func,
  matchedString: PropTypes.string,
  unlockPrice: PropTypes.number,
  canViewMedia: PropTypes.bool,
  fetchDrmLicenseAvailability: PropTypes.func,
  shouldShowLiveStream: PropTypes.bool,
  shouldShowOnline: PropTypes.bool,
  isIntersecting: PropTypes.bool,
};

FlixCard.defaultProps = {
  categoryIndex: null,
  categoryId: null,
  title: '',
  caption: '',
  messageId: '',
  shouldShowUsername: false,
  shouldShowAvatar: false,
  shouldTrackEvent: false,
  shouldUseLazyImage: true,
  enableTooltip: false,
  shouldPlay: false,
  isPro: false,
  hasFreePeek: false,
  isOnFreezone: false,
  hasAmateur: false,
  isAuthed: false,
  shouldShowUnlockCount: false,
  userId: '',
  username: '',
  badges: null,
  fetchMessageDetail: () => null,
  matchedString: '',
  unlockPrice: null,
  canViewMedia: false,
  fetchDrmLicenseAvailability: () => null,
  shouldShowLiveStream: true,
  shouldShowOnline: true,
  isIntersecting: false,
};

const StyledFlixCard = styled.section`
  background: ${color.black};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  position: relative;
  /* Can't set overflow hidden now because it crops Tooltips */
  /* overflow: hidden; */

  &:hover {
    opacity: 1;
  }
`;

const AvatarWrapper = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: flex-start;
`;

const AvatarBlock = styled.div`
  display: block;
  :hover {
    opacity: 1;
  }
`;

const TooltipWrapper = styled.div`
  margin-top: 2px;
  padding: 4px;
  border-radius: 2px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  background-color: rgb(246, 246, 246, 0.8);
  color: ${color.black};
  font-size: 12px;
  line-height: 1;
`;

const Caption = styled.div.attrs(({ isPlaceholder }) => ({
  style: {
    backgroundColor: isPlaceholder ? color.neutral[10] : 'transparent',
  },
}))`
  min-height: 16px;
  color: ${textColor.white};
  font-weight: 600;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 4px;

  overflow: hidden;
  display: -webkit-box;
  white-space: pre-line;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const MetaData = styled.div`
  height: 16px;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const ViewsNumber = styled.div`
  padding-left: 4px;
  font-size: 12px;
  color: ${color.neutral[30]};
`;

const UnLockNumber = styled.div`
  padding-left: 4px;
  font-size: 12px;
  color: ${color.neutral[30]};
`;

const LikeRateNumber = styled.div`
  padding-left: 4px;
  font-size: 12px;
  color: ${color.neutral[30]};
`;

const ProIconWrapper = styled.div`
  position: relative;
  width: 12px;
  height: 12px;
  margin-right: 4px;
  display: inline-block;
`;

const ProIcon = styled.img`
  position: absolute;
  top: 2px;
  left: 0%;
`;

const ViewsIcon = styled.img`
  opacity: 0.4;
`;

const UnLockIcon = styled.img`
  opacity: 0.4;
`;

const LikeIcon = styled.img`
  margin-left: 8px;
  opacity: 0.4;
`;

const InfoWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Info = styled.div`
  padding: 12px 8px;
  width: 100%;
  display: flex;
  flex: auto;
`;

const InfoRight = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  overflow: hidden;
  &:hover {
    opacity: 1;
  }
`;

const Username = styled.div.attrs(({ isPlaceholder }) => ({
  style: {
    backgroundColor: isPlaceholder ? color.neutral[10] : 'transparent',
    width: `${isPlaceholder ? 30 : 100}%`,
  },
}))`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 16px;
  margin-bottom: 4px;
  overflow: hidden;
`;

const UsernameText = styled.div`
  ${TextEllipsis};
  font-size: 14px;
  color: ${textColor.white};
`;
const UserStatusIndicatorWrapper = styled.div`
  margin-left: 4px;
`;

const UnlockPriceImg = styled.img`
  width: 16px;
  height: 16px;
  vertical-align: middle;
  margin-right: 2px;
`;

const UnlockPriceText = styled.span`
  vertical-align: middle;
`;

const UnlockPriceWrapper = styled.div`
  position: relative;
  font-size: 12px;
  color: ${color.tealBlue};
`;

export default withFetchTranslation(FlixCard);
